'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface SignUpInputs {
  name: string;
  email: string;
  password: string;
  terms: boolean;
}

interface SignUpFormProps {
  onSwitch: () => void;
}

export default function SignUpForm({ onSwitch }: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<string | null>(null);
  const router = useRouter();
  const { signUp, signInWithOAuth } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpInputs>();

  const password = watch('password', '');

  const onSubmit = async (data: SignUpInputs) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, { fullName: data.name });
      toast.success('Account created!', { description: 'Welcome to ChangedSince.' });
      router.push('/onboarding');
    } catch (err: any) {
      toast.error(err?.message || 'Could not create account. Please try again.');
    }
    setIsLoading(false);
  };

  const handleOAuth = async (provider: 'google' | 'apple') => {
    setOauthLoading(provider);
    try {
      await signInWithOAuth(provider);
      // Browser will redirect — no further action needed
    } catch (err: any) {
      toast.error(err?.message || 'OAuth sign-up failed. Please try again.');
      setOauthLoading(null);
    }
  };

  const passwordStrength = password.length === 0 ? 0 : password.length < 8 ? 1 : password.length < 12 ? 2 : 3;
  const strengthLabels = ['', 'Weak', 'Good', 'Strong'];
  const strengthColors = ['', 'bg-critical', 'bg-warning', 'bg-positive'];

  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground mb-1">Create account</h2>
      <p className="text-sm text-muted-foreground mb-8">
        Start watching what matters to you.
      </p>

      {/* Social auth */}
      <div className="space-y-3 mb-6">
        {[
          {
            label: 'Continue with Google',
            provider: 'google' as const,
            icon: (
              <svg width="18" height="18" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M47.532 24.552c0-1.636-.132-3.2-.388-4.704H24.48v8.898h12.932c-.556 3.002-2.244 5.546-4.782 7.254v6.026h7.744c4.532-4.174 7.158-10.32 7.158-17.474z" fill="#4285F4"/>
                <path d="M24.48 48c6.494 0 11.942-2.152 15.924-5.834l-7.744-6.026c-2.15 1.44-4.898 2.29-8.18 2.29-6.294 0-11.624-4.25-13.528-9.962H3.008v6.224C6.974 42.892 15.13 48 24.48 48z" fill="#34A853"/>
                <path d="M10.952 28.468A14.46 14.46 0 0 1 10.2 24c0-1.556.268-3.068.752-4.468v-6.224H3.008A23.96 23.96 0 0 0 .48 24c0 3.868.928 7.528 2.528 10.692l7.944-6.224z" fill="#FBBC05"/>
                <path d="M24.48 9.57c3.546 0 6.726 1.218 9.228 3.612l6.918-6.918C36.414 2.378 30.974 0 24.48 0 15.13 0 6.974 5.108 3.008 13.308l7.944 6.224C12.856 13.82 18.186 9.57 24.48 9.57z" fill="#EA4335"/>
              </svg>
            ),
          },
          {
            label: 'Continue with Apple',
            provider: 'apple' as const,
            icon: (
              <svg width="18" height="18" viewBox="0 0 256 315" xmlns="http://www.w3.org/2000/svg">
                <path d="M213.803 167.03c.442 47.58 41.74 63.413 42.197 63.615-.35 1.116-6.599 22.563-21.757 44.716-13.104 19.153-26.705 38.235-48.13 38.63-21.05.388-27.82-12.483-51.888-12.483-24.061 0-31.582 12.088-51.51 12.871-20.68.783-36.428-20.522-49.64-39.618C5.077 235.697-14.923 163.76 13.267 115.49c13.973-23.89 38.945-39.017 66.073-39.405 20.64-.39 40.12 13.881 52.757 13.881 12.633 0 36.33-17.17 61.2-14.645 10.424.435 39.692 4.214 58.48 31.711l-1.974 1.998zm-71.461-164.51C155.258 17.335 162.03 0 162.03 0c-14.898.56-32.498 9.927-43.054 22.416-9.474 11.202-17.783 29.208-15.548 46.48 16.28 1.266 32.93-8.197 42.914-20.376z" fill="#000000"/>
              </svg>
            ),
          },
        ].map((p) => (
          <button
            key={`social-signup-${p.label}`}
            disabled={!!oauthLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border rounded-xl text-sm font-semibold text-foreground hover:bg-muted active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={() => handleOAuth(p.provider)}
          >
            {oauthLoading === p.provider ? (
              <span className="w-4 h-4 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
            ) : (
              <span className="flex items-center justify-center w-5 h-5">{p.icon}</span>
            )}
            {p.label}
          </button>
        ))}
      </div>

      <div className="relative flex items-center mb-6">
        <div className="flex-1 h-px bg-border" />
        <span className="px-4 text-xs text-muted-foreground font-medium">or sign up with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="signup-name" className="block text-sm font-semibold text-foreground mb-1.5">
            Full name
          </label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            {...register('name', { required: 'Name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
            className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground bg-card placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
              errors.name ? 'border-critical' : 'border-input'
            }`}
            placeholder="Your full name"
          />
          {errors.name && <p className="text-xs text-critical mt-1.5 font-medium">{errors.name.message}</p>}
        </div>

        {/* Email */}
        <div>
          <label htmlFor="signup-email" className="block text-sm font-semibold text-foreground mb-1.5">
            Email address
          </label>
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
            className={`w-full px-4 py-3 rounded-xl border text-sm text-foreground bg-card placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
              errors.email ? 'border-critical' : 'border-input'
            }`}
            placeholder="you@example.com"
          />
          {errors.email && <p className="text-xs text-critical mt-1.5 font-medium">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="block text-sm font-semibold text-foreground mb-1.5">
            Password
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Must be at least 8 characters</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
              className={`w-full px-4 py-3 pr-12 rounded-xl border text-sm text-foreground bg-card placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                errors.password ? 'border-critical' : 'border-input'
              }`}
              placeholder="Create a strong password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground rounded-lg transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-critical mt-1.5 font-medium">{errors.password.message}</p>}
          {passwordStrength > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex gap-1 flex-1">
                {[1, 2, 3].map((level) => (
                  <div
                    key={`strength-${level}`}
                    className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                      level <= passwordStrength ? strengthColors[passwordStrength] : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
              <span className={`text-xs font-semibold ${
                passwordStrength === 1 ? 'text-critical' : passwordStrength === 2 ? 'text-warning' : 'text-positive'
              }`}>
                {strengthLabels[passwordStrength]}
              </span>
            </div>
          )}
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              {...register('terms', { required: 'You must accept the terms to continue' })}
              className="w-4 h-4 mt-0.5 rounded border-input accent-primary flex-shrink-0"
            />
            <span className="text-sm text-muted-foreground leading-relaxed">
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline">Privacy Policy</a>
            </span>
          </label>
          {errors.terms && <p className="text-xs text-critical mt-1.5 font-medium">{errors.terms.message}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-3 rounded-xl text-sm font-bold text-primary-foreground teal-gradient hover:opacity-90 active:scale-95 transition-all duration-150 flex items-center justify-center gap-2 ${
            isLoading ? 'opacity-70 cursor-not-allowed' : ''
          }`}
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Creating account…
            </>
          ) : (
            'Create account'
          )}
        </button>
      </form>

      <p className="text-sm text-muted-foreground text-center mt-6">
        Already have an account?{' '}
        <button onClick={onSwitch} className="text-primary font-semibold hover:underline">
          Sign in
        </button>
      </p>
    </div>
  );
}